import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  type CustomBlockingRulesOption,
  InventoryContentOption,
  type PublisherInventoryRule,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { type JSX, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { useInventoryAndContentLabels } from '../../shared/InventoryAndContent/constants.ts';
import { CustomBlockingRulesItem } from './CustomBlockingRulesItem.tsx';
import type { CustomBlockingRulesItemsProps } from './CustomBlockingRulesItems.tsx';

export type CustomBlockingRulesItemsRendererProps = CustomBlockingRulesItemsProps & {
  onRemoveAll: (type: CustomBlockingRulesOption) => void;
  onRemoveSelectedRule: (customRule: PublisherInventoryRule) => void;
  onRemoveSingleEntry: (item: string | JSX.Element, customRule: PublisherInventoryRule) => void;
  type: CustomBlockingRulesOption;
};

export const CustomBlockingRulesItemsRenderer = ({
  isNotLastItem,
  onRemoveAll,
  onRemoveSelectedRule,
  onRemoveSingleEntry,
  readonly = true,
  switchState: chipLineSwitchState,
  targetingParams: selectedItems,
  type,
  disabledReasonMessage,
}: CustomBlockingRulesItemsRendererProps) => {
  const { t } = useTranslation();
  const labels = useInventoryAndContentLabels();
  const title = labels[InventoryContentOption.PUBLISHER_INVENTORY_RULES].label[type];

  const handleOnRemoveAll = useCallback(() => {
    onRemoveAll(type);
  }, [onRemoveAll, type]);

  return (
    <Box>
      <FiltersTitle
        onClear={() => handleOnRemoveAll()}
        title={`${title} [ ${selectedItems.length} ]`}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
        sx={{ mb: 1 }}
      />
      <Typography
        sx={{
          color: 'text.secondary',
          textTransform: 'uppercase',
        }}
      >
        {t('Blocked')}
      </Typography>

      <Box>
        {selectedItems.map(customRule => (
          <CustomBlockingRulesItem
            key={customRule.publisher_id + type}
            onRemoveAll={handleOnRemoveAll}
            onRemoveSelectedRule={onRemoveSelectedRule}
            onRemoveSingleEntry={onRemoveSingleEntry}
            readonly={readonly}
            customRule={customRule}
          />
        ))}
      </Box>

      {isNotLastItem && <DimensionChipLine switchState={chipLineSwitchState} />}
    </Box>
  );
};
